import styled, { keyframes } from 'styled-components';
import { theme } from '../../theme';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const ContactSection = styled.section`
  background: ${theme.colors.bg};
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse 50% 40% at 0% 50%, rgba(${theme.colors.primaryRgb}, 0.07) 0%, transparent 60%);
  }
`;

export const Container = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1fr 1.4fr;
  gap: 5rem;
  align-items: start;

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }

  @media (max-width: ${theme.breakpoints.md}) {
    padding: 0 1rem;
  }
`;

export const ContactInfo = styled.div`
  display: grid;
  gap: 1rem;
  animation: ${fadeIn} 0.6s ease both;
`;

export const SectionTag = styled.span`
  display: inline-block;
  padding: 0.3rem 0.85rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${theme.colors.primaryLight};
  background: rgba(${theme.colors.primaryRgb}, 0.1);
  border: 1px solid rgba(${theme.colors.primaryRgb}, 0.25);
  margin-bottom: 1rem;
`;

export const SectionTitle = styled.h2`
  font-size: clamp(1.8rem, 4vw, 2.8rem);
  font-weight: 700;
  letter-spacing: -0.06em;
  color: ${theme.colors.text};
  margin-bottom: 1rem;
  line-height: 1.1;

  span {
    background: ${theme.colors.gradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

export const SectionSubtitle = styled.p`
  font-size: 0.95rem;
  color: ${theme.colors.textMuted};
  line-height: 1.75;
  margin-bottom: 0.2rem;
`;

export const ContactHighlights = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

export const HighlightCard = styled.div`
  padding: 1rem 1.05rem;
  border-radius: 20px;
  border: 1px solid rgba(148, 163, 184, 0.12);
  background: rgba(8, 12, 18, 0.66);

  span {
    display: block;
    margin-bottom: 0.35rem;
    color: ${theme.colors.textMuted};
    font-family: 'Space Grotesk', 'Manrope', sans-serif;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  strong {
    color: ${theme.colors.textSecondary};
    font-size: 0.92rem;
    line-height: 1.5;
  }
`;

export const ContactList = styled.div`
  display: grid;
  gap: 0.75rem;
`;

export const ContactItem = styled.a`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.05rem;
  border-radius: 18px;
  border: 1px solid ${theme.colors.border};
  background: linear-gradient(180deg, rgba(17, 25, 34, 0.96), rgba(10, 15, 22, 0.9));
  transition: ${theme.transitions.default};
  color: ${theme.colors.text};

  &:hover {
    border-color: rgba(${theme.colors.primaryRgb}, 0.35);
    transform: translateX(4px);
  }
`;

export const ContactIcon = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 14px;
  background: ${theme.colors.gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
  font-size: 1rem;
`;

export const ContactText = styled.div`
  .label {
    font-size: 0.75rem;
    color: ${theme.colors.textMuted};
    margin-bottom: 0.1rem;
  }
  .value {
    font-size: 0.9rem;
    font-weight: 500;
    color: ${theme.colors.text};
  }
`;

export const FormCard = styled.form`
  background: linear-gradient(180deg, rgba(17, 25, 34, 0.98), rgba(10, 15, 22, 0.92));
  border: 1px solid rgba(148, 163, 184, 0.12);
  border-radius: 28px;
  padding: 2.5rem;
  animation: ${fadeIn} 0.6s ease 0.15s both;

  @media (max-width: ${theme.breakpoints.md}) {
    padding: 1.35rem;
    border-radius: 22px;
  }
`;

export const FormIntro = styled.div`
  margin-bottom: 1.4rem;

  span {
    display: inline-flex;
    margin-bottom: 0.45rem;
    color: ${theme.colors.primaryLight};
    font-family: 'Space Grotesk', 'Manrope', sans-serif;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
  }

  h3 {
    margin-bottom: 0.4rem;
    font-size: clamp(1.4rem, 3vw, 1.9rem);
    font-weight: 700;
    letter-spacing: -0.04em;
    color: ${theme.colors.text};
  }

  p {
    color: ${theme.colors.textMuted};
    font-size: 0.92rem;
    line-height: 1.7;
  }
`;

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

export const FormGroup = styled.div`
  margin-bottom: 1.25rem;

  label {
    display: block;
    font-size: 0.8rem;
    font-weight: 500;
    color: ${theme.colors.textMuted};
    margin-bottom: 0.4rem;
    letter-spacing: 0.3px;
  }
`;

export const Input = styled.input`
  width: 100%;
  padding: 0.88rem 1rem;
  background: ${theme.colors.bgSecondary};
  border: 1px solid ${theme.colors.border};
  border-radius: 14px;
  color: ${theme.colors.text};
  font-size: 0.9rem;
  transition: ${theme.transitions.default};

  &::placeholder { color: ${theme.colors.textMuted}; opacity: 0.6; }

  &:focus {
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(${theme.colors.primaryRgb}, 0.12);
  }
`;

export const Textarea = styled.textarea`
  width: 100%;
  padding: 0.88rem 1rem;
  background: ${theme.colors.bgSecondary};
  border: 1px solid ${theme.colors.border};
  border-radius: 14px;
  color: ${theme.colors.text};
  font-size: 0.9rem;
  resize: vertical;
  min-height: 140px;
  transition: ${theme.transitions.default};

  &::placeholder { color: ${theme.colors.textMuted}; opacity: 0.6; }

  &:focus {
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(${theme.colors.primaryRgb}, 0.12);
  }
`;

export const SubmitBtn = styled.button<{ $loading?: boolean }>`
  width: 100%;
  min-height: 58px;
  padding: 0.95rem;
  font-family: 'Space Grotesk', 'Manrope', sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  color: white;
  background: ${theme.colors.gradient};
  border-radius: 16px;
  transition: ${theme.transitions.default};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  opacity: ${({ $loading }) => ($loading ? 0.7 : 1)};
  pointer-events: ${({ $loading }) => ($loading ? 'none' : 'all')};

  &:hover {
    opacity: 0.88;
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.glow};
  }
`;

export const SuccessMsg = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 1rem;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 10px;
  color: #34d399;
  font-size: 0.9rem;
  font-weight: 500;
  margin-top: 1rem;
`;
